import { Box, FormControl, FormHelperText, Grid, Paper, WithStyles } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import * as React from "react";
import Typography from "@material-ui/core/Typography/Typography";
import { useTranslation } from "react-i18next";
import { styles } from "./ProductDetailsHistoricalBox.styles";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { IntervalRange } from "../../components/TimeRange/IntervalRange";
import { hasMinimalDaysError, isDateInvalid, isDateOutsideRange } from "../../../utilities/dateTimeUtils";
import {
    dateInvalidError,
    dateOutsideRangeError,
    filesEmptyError,
    minimalDaysError
} from "./helpers/productDetailsHelper";
import { ModulePricingInfoDialog } from "./360T/ModulePricingInfoDialog";
import { useModulePricing } from "../../../hooks/useModulePricing";

export interface IPricesHistorical {
    startingAt: number;
    currency: string;
    minimalDays: number;
    firstAvailableDate: string;
    lastAvailableDate: string;
    total: number;
    files: number;
    numberOfModules: number;
}

export interface IHistoricalBoxProps extends WithStyles<typeof styles> {
    disabled: boolean;
    prices: IPricesHistorical;
    handleCheckout: any;
    handleDateChange: any;
    rowId: any;
    from: string;
    to: string;
    headlineDisabled?: boolean;
    remark?: string;
    showBusinessDays?: boolean;
}

const HistoricalBoxPure: React.FunctionComponent<IHistoricalBoxProps> = ({
    handleCheckout,
    handleDateChange,
    disabled,
    prices,
    rowId,
    from,
    to,
    headlineDisabled,
    remark,
    showBusinessDays,
    classes
}: IHistoricalBoxProps) => {
    const { t } = useTranslation();
    const {useHasProductModulePricing} = useModulePricing();

    const handleTimeInterval = (from, to) => {
        handleDateChange(from, to);
    };

    const handleClick = () => {
        handleCheckout();
    };

    const hasModulePricing = useHasProductModulePricing(rowId);

    return (
        <>
            <Grid item xs={6} key={"historical-box-" + rowId}>
                <Paper elevation={0} className={classes.paper}>
                    <Box margin={4} className={classes.card}>
                        {headlineDisabled ? null : <Typography variant="h2">One-off order</Typography>}
                        <FormControl style={{ width: "100%" }}>
                            <>
                                <IntervalRange
                                    disabled={disabled}
                                    from={from}
                                    to={to}
                                    firstAvailableDate={prices.firstAvailableDate}
                                    lastAvailableDate={prices.lastAvailableDate}
                                    translation={t}
                                    onChange={handleTimeInterval}
                                />
                                {prices.files === 0 ? null : (
                                    <>
                                        {hasModulePricing ? (
                                            <>
                                                <Typography variant="body1">
                                                    <b>Business days:</b> {prices.files}
                                                </Typography>
                                                <Grid
                                                    container
                                                    spacing={2}
                                                    className={classes.marginTop}
                                                    alignItems="center"
                                                >
                                                    <Grid item xs={10}>
                                                        <Typography variant="body1">
                                                            <b>Number of modules:</b> {prices.numberOfModules}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={2} className={classes.alignRight}>
                                                        <ModulePricingInfoDialog product={rowId} />
                                                    </Grid>
                                                </Grid>
                                            </>
                                        ) : (
                                            <>
                                                <Typography className={classes.price} variant="body1">
                                                    <b>
                                                        {prices.currency}
                                                        {Number(prices.total).toFixed(2)}
                                                    </b>
                                                </Typography>
                                                <Typography variant="body1">
                                                    <b>{showBusinessDays ? "Business days" : "Files"}:</b>{" "}
                                                    {prices.files}
                                                </Typography>
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        </FormControl>
                        <div className={classes.buttonCheckout}>
                            <BlueButton
                                disabled={
                                    isDateInvalid(from, to) ||
                                    hasMinimalDaysError(prices.minimalDays, prices.files) ||
                                    isDateOutsideRange(from, to, prices.firstAvailableDate, prices.lastAvailableDate) ||
                                    prices.files === 0
                                }
                                fullWidth
                                onClick={handleClick}
                            >
                                {t("dataView.addToCart")}
                            </BlueButton>
                            {remark !== "" ? <FormHelperText error={true}>
                                <span dangerouslySetInnerHTML={{ __html: remark }} />
                            </FormHelperText> : null}
                            {dateInvalidError(from, to) ??
                                filesEmptyError(prices.files) ??
                                minimalDaysError(prices.minimalDays, prices.files) ??
                                dateOutsideRangeError(from, to, prices.firstAvailableDate, prices.lastAvailableDate)}
                        </div>
                    </Box>
                </Paper>
            </Grid>
        </>
    );
};

export const ProductDetailsHistoricalBox = withStyles(styles)(HistoricalBoxPure);
